import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useCallback } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DateTimePicker from "../../../../Components/DateTimePicker";
import LBMModal from "../../../../Components/LBMDialog";
import SaveOrCancelButtons from "../../../../Components/SaveOrCancelButtons";
import { Package } from "../../../../Types/Package";
import { convertLocalTimeZoneToUTC, convertToDateTime } from "../../../../Utilities/useDateUtils";
import useAlert from '../../../../Alerts/useAlert';

interface AddPackagesDialogProps {
    showDialog: boolean,
    closeDialog: () => void,
    packageTypeOptions: { value: string, display: string; }[],
    addPackage: (item: Package) => void;
}

type WithId<T extends object> = {
    id: number;
    data: T
}

function AddPackagesDialog({ showDialog, closeDialog, packageTypeOptions, addPackage }: AddPackagesDialogProps) {
    const { t } = useTranslation()
    const { setAlert } = useAlert();

    const isValidPackage = (p: Partial<Package>): p is Package => {
        if (typeof p.containerNumber === 'string' && p.containerNumber !== ""
            && typeof p.packageDateTimeUtc === 'string' && p.containerNumber !== ""
            && typeof p.grade === 'string' && p.grade !== ""
            && typeof p.netWeight === 'number' && p.netWeight > 0
            && typeof p.tareWeight === 'number' && p.tareWeight > 0
            && typeof p.packageType === 'string' && p.packageType !== ""
            && typeof p.scale === 'string' && p.scale !== ""
        ) {
            return true;
        }

        return false;
    }

    const [formErrors, setFormErrors] = useState<any[]>([]);

    const savePackages = () => {
        setFormErrors([]);
        const inValidPackages = newPackages.filter(p => !isValidPackage(p.data));
        if (inValidPackages.length === 0) {
            newPackages.forEach((p) => {
                addPackage(p.data as Package);
            });

            setAlert("success", t("LotDetails.Packages.Added"), t("LotDetails.Packages.AddedMessage"))
            closeDialog();
        } else {
            inValidPackages.forEach(p => {
                let formError:any = { id: p.id };
                if (!p.data.containerNumber || p.data.containerNumber === "") {
                    formError.containerNumber = "Validation.CannotBeEmpty";
                }
                if (!p.data.packageDateTimeUtc || p.data.packageDateTimeUtc === "") {
                    formError.packageDateTimeUtc = "Validation.CannotBeEmpty";
                }
                if (!p.data.grade || p.data.grade === "") {
                    formError.grade = "Validation.CannotBeEmpty";
                }
                if (!p.data.packageType || p.data.packageType === "") {
                    formError.packageType = "Validation.CannotBeEmpty";
                }
                if (!p.data.scale || p.data.scale === "") {
                    formError.scale = "Validation.CannotBeEmpty";
                }
                if (p.data.tareWeight === undefined) {
                    formError.tareWeight = "Validation.CannotBeEmpty";
                } else {
                    if (p.data.tareWeight === 0) {
                        formError.tareWeight = "Validation.CannotBeZero";
                    }
                    if (p.data.tareWeight < 0) {
                        formError.tareWeight = "Validation.CannotBeNegative";
                    }
                }

                if (p.data.netWeight === undefined) {
                    formError.netWeight = "Validation.CannotBeEmpty";
                } else {
                    if (p.data.netWeight === 0) {
                        formError.netWeight = "Validation.CannotBeZero";
                    }
                    if (p.data.netWeight < 0) {
                        formError.netWeight = "Validation.CannotBeNegative";
                    }
                }

                setFormErrors(e => [...e, formError]);
            });
        }
    }

    const updatePackage = (id:number, field: keyof Package, value: any) => {
        setNewPackages(packages => packages.map(p => p.id === id ? { ...p, data: { ...p.data, [field]: value } } : p));
    }

    const deletePackage = (id: number) => {
        setNewPackages(packages => packages.filter(p => p.id !== id));
        setFormErrors(errors => errors.filter(e => e.id !== id));
    }

    const getNewPackage = useCallback((packages: WithId<Package>[]) => {
        let id = 1;
        if (packages.length > 0) {
            id = packages.map(p => p.id).reduce((current, max) => current > max ? current : max) + 1;
        }

        return {
            id: id, data: {
                containerNumber: "",
                packageDateTimeUtc: "",
                grade:"",
                status: "",
                productPackageCode:"",
                packageType: "",
                tareWeight: 0,
                netWeight: 0,
                scale:"",
                redDotCode: "",
                notes:"",

            }
        };
    }, []);

    const [newPackages, setNewPackages] = useState<WithId<Package>[]>([getNewPackage([])]);

    return (
        <LBMModal
            className="verywide-dialog"
            showDialog={showDialog}
            closeDialog={closeDialog}
            titleTranslationKey="LotDetails.Packages.AddPackages"
            footerComponent={<SaveOrCancelButtons closeDialog={closeDialog} save={savePackages} />}
        >
            <Form>
                <Form.Group>
                    <Row className="gx-0">
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.BoxNumber")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.PackageDateTime")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.Grade")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.Status")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.PackageType")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.TareWeight")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.NetWeight")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.Scale")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.RedDot")}</Form.Label>
                        <Form.Label as={Col}>{t<string>("LotDetails.Packages.Notes")}</Form.Label>
                    </Row>
                    {newPackages.map(row => {
                        let formError = formErrors.find(e => e.id === row.id);
                        return (
                            <Row className="gx-0" key={`row_${row.id}`}>
                                <Col>
                                    <Form.Control value={row.data.containerNumber ?? ""} onChange={(e) => updatePackage(row.id, "containerNumber", e.target.value)} isInvalid={formError?.containerNumber}></Form.Control>
                                    <Form.Control.Feedback type="invalid">{t<string>(formError?.containerNumber)}</Form.Control.Feedback>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <DateTimePicker name="" dateTime={convertToDateTime(row.data.packageDateTimeUtc)?.toJSDate() ?? null} handleDateSelect={(date) => updatePackage(row.id, "packageDateTimeUtc", convertLocalTimeZoneToUTC(date).toISO({suppressMilliseconds:true}))} showIcon={false} isInvalid={formError?.packageDateTimeUtc}></DateTimePicker>
                                        <Form.Control.Feedback type="invalid">{t<string>(formError?.packageDateTimeUtc)}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Control value={row.data.grade ?? ""} onChange={(e) => updatePackage(row.id, "grade", e.target.value)} isInvalid={formError?.grade}></Form.Control>
                                    <Form.Control.Feedback type="invalid">{t<string>(formError?.grade)}</Form.Control.Feedback>
                                </Col>
                                <Col>
                                    <Form.Control value={row.data.status ?? ""} onChange={(e) => updatePackage(row.id, "status", e.target.value)}></Form.Control>
                                </Col>
                                <Col>
                                    <Form.Select
                                        value={row.data.packageType ?? ""}
                                        onChange={(e) => updatePackage(row.id, "packageType", e.target.value)}
                                        isInvalid={formError?.packageType}>
                                        <option value={""}>Please Select</option>
                                        {packageTypeOptions.map((option: any, index: number) => (
                                            <option key={`dialog-package-type-option-${index}`} value={option.value}>
                                                {option.display}
                                            </option>))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{t<string>(formError?.packageType)}</Form.Control.Feedback>
                                </Col>
                                <Col>
                                    <Form.Control type="number" value={row.data.tareWeight} onChange={(e) => updatePackage(row.id, "tareWeight", +e.target.value)} isInvalid={formError?.tareWeight}></Form.Control>
                                    <Form.Control.Feedback type="invalid">{t<string>(formError?.tareWeight)}</Form.Control.Feedback>
                                </Col>
                                <Col>
                                    <Form.Control type="number" value={row.data.netWeight} onChange={(e) => updatePackage(row.id, "netWeight", +e.target.value)} isInvalid={formError?.netWeight}></Form.Control>
                                    <Form.Control.Feedback type="invalid">{t<string>(formError?.netWeight)}</Form.Control.Feedback>
                                </Col>
                                <Col>
                                    <Form.Control value={row.data.scale ?? ""} onChange={(e) => updatePackage(row.id, "scale", e.target.value)} isInvalid={formError?.scale}></Form.Control>
                                    <Form.Control.Feedback type="invalid">{t<string>(formError?.scale)}</Form.Control.Feedback>
                                </Col>
                                <Col>
                                    <Form.Control value={row.data.redDotCode ?? ""} onChange={(e) => updatePackage(row.id, "redDotCode", e.target.value)}></Form.Control>
                                </Col>
                                <Col>
                                    <Form.Control value={row.data.notes ?? ""} onChange={(e) => updatePackage(row.id, "notes", e.target.value)}></Form.Control>
                                </Col>
                                <Col md={"auto"} className="ms-1 align-content-center">
                                    <Button variant={"outline-light"} onClick={() => deletePackage(row.id)} className={"border-0"}>
                                        <FontAwesomeIcon icon={faTrash} className="text-danger" />
                                    </Button>
                                </Col>
                            </Row>);
                    })}
                </Form.Group>
                <Form.Group className="mt-3">
                    <Button onClick={()=> setNewPackages(p => [...p, getNewPackage(p)])}>
                        {t("Application.Controls.Add")}
                    </Button>
                </Form.Group>
            </Form>
        </LBMModal>
    );
}

export default AddPackagesDialog;